/**
 * Pure pricing selectors over the FROZEN money snapshots stored on each service /
 * line item (all integer cents). Nothing here recomputes from labor rates or fees
 * — that live recomputation is what let historical repair orders drift, and is
 * exactly what these selectors exist to replace.
 *
 * Prefer these over inline `services.reduce(...)` in components so that the three
 * decisions that keep going wrong are made in ONE place:
 *   1. which field   — grand total (tax-inc) vs subtotal (pre-tax) vs tax
 *   2. which services — All / ExceptDeclined / Billable / AuthorizedOnly
 *   3. null guards    — every getter tolerates missing values
 *
 * Field reference (see MONEY_HANDLING.md):
 *   Service.totalCents      = pre-tax subtotal
 *   Service.totalTaxCents   = tax
 *   Service.grandTotalCents = tax-inclusive total   <-- the customer-facing "total"
 *   ServiceItem.totalCents  = tax-inclusive line total
 *   Payment.amountCents     = amount paid
 *
 * Everything returns integer cents; render with <Price isInCents /> (or divide by
 * 100 only at an external boundary such as a payment amount input).
 */
import Service from 'models/ServiceModel';
import ServiceItem from 'models/ServiceItemModel';
import Payment from 'models/PaymentModel';
import ServiceItemType from 'models/types/ServiceItemType';
import ServiceAuthorizationStatus from 'models/types/ServiceAuthorizationStatus';

/** Which services a roll-up counts. Pick the rule intentionally per screen. */
export enum ServiceInclusion {
    /** Every service. */
    All = 'all',
    /** Everything except declined work (e.g. the job board card). */
    ExceptDeclined = 'exceptDeclined',
    /** What is actually billed: authorized work plus any non-recommended work
     *  (e.g. the invoice / estimate grand total). */
    Billable = 'billable',
    /** Only services the customer explicitly authorized. */
    AuthorizedOnly = 'authorizedOnly',
}

const includes = (service: Service, inclusion: ServiceInclusion): boolean => {
    switch (inclusion) {
        case ServiceInclusion.All:
            return true;
        case ServiceInclusion.ExceptDeclined:
            return service.authorizationStatus !== ServiceAuthorizationStatus.Declined;
        case ServiceInclusion.Billable:
            return !(service.isRecommended && service.authorizationStatus !== ServiceAuthorizationStatus.Authorized);
        case ServiceInclusion.AuthorizedOnly:
            return service.authorizationStatus === ServiceAuthorizationStatus.Authorized;
    }
    return false;
};

const includedServices = (services: Service[] | undefined, inclusion: ServiceInclusion): Service[] =>
    (services ?? []).filter((service) => includes(service, inclusion));

// ---- Single service ---------------------------------------------------------

export const serviceSubtotalCents = (service: Service): number => service.totalCents || 0;
export const serviceTaxCents = (service: Service): number => service.totalTaxCents || 0;
/** Tax-inclusive total — the customer-facing amount for a service. */
export const serviceGrandTotalCents = (service: Service): number => service.grandTotalCents || 0;

// ---- Single line item -------------------------------------------------------

/** ServiceItem.totalCents is already tax-inclusive at the line level. */
export const lineItemTotalCents = (item: ServiceItem): number => item.totalCents || 0;

// ---- Repair-order roll-ups (choose an inclusion rule) -----------------------

export const roSubtotalCents = (services: Service[] | undefined, inclusion = ServiceInclusion.All): number =>
    includedServices(services, inclusion).reduce((acc, s) => acc + serviceSubtotalCents(s), 0);

export const roTaxCents = (services: Service[] | undefined, inclusion = ServiceInclusion.All): number =>
    includedServices(services, inclusion).reduce((acc, s) => acc + serviceTaxCents(s), 0);

export const roGrandTotalCents = (services: Service[] | undefined, inclusion = ServiceInclusion.All): number =>
    includedServices(services, inclusion).reduce((acc, s) => acc + serviceGrandTotalCents(s), 0);

/** Sum of tax-inclusive line totals for one item type across the included services. */
export const roTotalByItemTypeCents = (
    services: Service[] | undefined,
    type: ServiceItemType,
    inclusion = ServiceInclusion.All,
): number =>
    includedServices(services, inclusion)
        .flatMap((service) => service.items ?? [])
        .filter((item) => item.type === type)
        .reduce((acc, item) => acc + lineItemTotalCents(item), 0);

// ---- Payments ---------------------------------------------------------------

export const paidToDateCents = (payments: Payment[] | undefined): number =>
    (payments ?? []).reduce((acc, payment) => acc + (payment.amountCents || 0), 0);

/** Outstanding balance (never negative), tax-inclusive. */
export const balanceDueCents = (
    services: Service[] | undefined,
    payments: Payment[] | undefined,
    inclusion = ServiceInclusion.All,
): number => Math.max(0, roGrandTotalCents(services, inclusion) - paidToDateCents(payments));
